import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query } from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../firebase";

// Local color helpers (mirrors Batches.tsx)
const getColorClasses = (color: string) => {
  const colorMap: Record<
    string,
    { border: string; bg: string; bgLight: string; text: string; hover: string }
  > = {
    blue: {
      border: "border-blue-500",
      bg: "bg-blue-500",
      bgLight: "bg-blue-100",
      text: "text-blue-600",
      hover: "hover:bg-blue-600",
    },
    green: {
      border: "border-green-500",
      bg: "bg-green-500",
      bgLight: "bg-green-100",
      text: "text-green-600",
      hover: "hover:bg-green-600",
    },
    purple: {
      border: "border-purple-500",
      bg: "bg-purple-500",
      bgLight: "bg-purple-100",
      text: "text-purple-600",
      hover: "hover:bg-purple-600",
    },
    red: {
      border: "border-red-500",
      bg: "bg-red-500",
      bgLight: "bg-red-100",
      text: "text-red-600",
      hover: "hover:bg-red-600",
    },
  };

  return colorMap[color] || colorMap.blue;
};

const colorForClass: Record<string, "blue" | "green" | "purple" | "red"> = {
  IX: "blue",
  X: "green",
  XI: "purple",
  XII: "red",
};

const levelForClass = (cls: string) =>
  cls === "IX" || cls === "X" ? "O Level" : "A Level";

export type FirestoreBatch = {
  id: string;
  name: string;
  class: "IX" | "X" | "XI" | "XII" | string;
  location: string;
  schedule: string;
  startDate?: string;
  maxStudents?: number;
  curriculum?: string;
  createdAt?: any;
};

const AllBatches: React.FC = () => {
  const [items, setItems] = useState<FirestoreBatch[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "batches"));
    const unsub = onSnapshot(q, (snap) => {
      const data: FirestoreBatch[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<FirestoreBatch, "id">),
      }));
      // sort by createdAt desc if available
      data.sort((a, b) => {
        const ad = (a as any).createdAt?.seconds || 0;
        const bd = (b as any).createdAt?.seconds || 0;
        return bd - ad;
      });
      setItems(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div className="text-left">
            <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">All Batches</h1>
            <p className="mt-2 text-gray-600">Browse all available and upcoming batches with details</p>
          </div>
          <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
            ← Back to Home
          </Link>
        </div>

        {loading ? (
          <div className="text-center text-gray-600">Loading batches...</div>
        ) : items.length === 0 ? (
          <div className="text-center text-gray-600">No batches found.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {items.map((b) => {
              const color = colorForClass[b.class] || "blue";
              return (
                <div
                  key={b.id}
                  className={`bg-white rounded-lg shadow-md overflow-hidden border-t-4 ${getColorClasses(color).border} hover:shadow-lg transition duration-300`}
                >
                  <div className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{b.name || `Class ${b.class}`}</h3>
                        <p className={`inline-block px-2 py-1 rounded-full text-xs font-semibold text-gray-800 ${getColorClasses(color).bgLight} mt-2`}>
                          {levelForClass(String(b.class))}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 space-y-3">
                      <div className="flex items-start">
                        <svg className="h-5 w-5 text-gray-400 mt-0.5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-600">{b.schedule || "Schedule will be announced"}</span>
                      </div>

                      <div className="flex items-start">
                        <svg className="h-5 w-5 text-gray-400 mt-0.5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M10 20s6-5.686 6-10a6 6 0 10-12 0c0 4.314 6 10 6 10zm0-8a2 2 0 110-4 2 2 0 010 4z" />
                        </svg>
                        <span className="text-gray-600">{b.location || "Location will be announced"}</span>
                      </div>

                      {b.startDate ? (
                        <div className="flex items-start">
                          <svg className="h-5 w-5 text-gray-400 mt-0.5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zM2 9v6a2 2 0 002 2h12a2 2 0 002-2V9H2zm5 2a1 1 0 012 0v3a1 1 0 11-2 0v-3z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-600">Starts: {new Date(b.startDate).toLocaleDateString()}</span>
                        </div>
                      ) : null}

                      {typeof b.maxStudents === "number" ? (
                        <div className="flex items-start">
                          <svg className="h-5 w-5 text-gray-400 mt-0.5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13 7a3 3 0 11-6 0 3 3 0 016 0zM4 14a4 4 0 018 0H4zM13 14a3 3 0 116 0h-6z" />
                          </svg>
                          <span className="text-gray-600">Max Students: {b.maxStudents}</span>
                        </div>
                      ) : null}

                      {b.curriculum ? (
                        <div className="flex items-start">
                          <svg className="h-5 w-5 text-gray-400 mt-0.5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2H4zm3 3h6v2H7V6zm0 4h6v2H7v-2z" />
                          </svg>
                          <span className="text-gray-600">Curriculum: {b.curriculum}</span>
                        </div>
                      ) : null}
                    </div>

                    <div className="mt-6">
                      <button className={`w-full ${getColorClasses(color).bg} ${getColorClasses(color).hover} text-white py-2 px-4 rounded-md font-medium transition duration-300`}>
                        Enroll Now
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default AllBatches;
